import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, fmtDate, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { FolderSimple, FileImage, FilePdf, FileDoc, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import HelpLink from "@/components/HelpLink";

const KINDS = [
  { value: "", label: "All" },
  { value: "photo", label: "Photos" },
  { value: "appraisal", label: "Appraisals" },
  { value: "manual", label: "Manuals" },
  { value: "document", label: "Documents" },
];

// Thumbnail dimensions in px + matching icon size. Kept small enough
// that Large still fits three-across on a laptop screen (>=1280px).
const THUMB_SIZES = {
  s: { box: 40, icon: 20, label: "S" },
  m: { box: 80, icon: 32, label: "M" },
  l: { box: 160, icon: 56, label: "L" },
};

function loadThumbSize() {
  try {
    const v = localStorage.getItem("fl.vault.thumbSize");
    return THUMB_SIZES[v] ? v : "s";
  } catch { return "s"; }
}

export default function Vault() {
  const { activeLedgerId, households } = useAuth();
  const activeHousehold = useMemo(
    () => (households || []).find((h) => h.id === activeLedgerId) || null,
    [households, activeLedgerId],
  );
  const [files, setFiles] = useState([]);
  const [kind, setKind] = useState("");
  const [rooms, setRooms] = useState([]);
  const [roomPhotos, setRoomPhotos] = useState({});
  const [room, setRoom] = useState(() => {
    // v3.15 — persist room selection so users walking a specific
    // space over multiple sessions don't have to re-set the filter.
    try { return localStorage.getItem("fl.vault.room") || ""; }
    catch { return ""; }
  });
  const setRoomPersisted = (v) => {
    setRoom(v);
    try {
      if (!v) localStorage.removeItem("fl.vault.room");
      else localStorage.setItem("fl.vault.room", v);
    } catch { /* ignore */ }
  };
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [thumbSize, setThumbSize] = useState(loadThumbSize);

  const setSize = (v) => {
    setThumbSize(v);
    try { localStorage.setItem("fl.vault.thumbSize", v); } catch { /* ignore */ }
  };

  const load = () => {
    setLoading(true);
    const params = {};
    if (kind) params.kind = kind;
    if (room) params.room = room;
    api.get("/vault", { params })
      .then((r) => setFiles(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, room]);

  // Load the household's rooms so we can render the filter dropdown.
  // Personal ledger (no active household) shows no room filter — files
  // there aren't organised by room. When the ledger switches, drop any
  // active room selection so we don't hide files that don't belong to a
  // room in the new ledger.
  useEffect(() => {
    if (!activeHousehold) { setRooms([]); setRoomPhotos({}); if (room) setRoomPersisted(""); return; }
    let cancelled = false;
    api.get(`/households/${activeHousehold.id}/rooms`)
      .then((r) => {
        if (cancelled) return;
        const next = r.data?.rooms || [];
        setRooms(next);
        setRoomPhotos(r.data?.room_photos || {});
        // v3.15.1 — clear a stale room selection carried over from a
        // previous household. Without this the dropdown reads "All
        // rooms" (no matching <option>) while every API call still
        // sends the stale ?room=X and the vault appears empty.
        if (room && !next.some((n) => n.toLowerCase() === room.toLowerCase())) {
          setRoomPersisted("");
        }
      })
      .catch(() => { if (!cancelled) { setRooms([]); setRoomPhotos({}); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeHousehold?.id]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind || "document");
    try {
      await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Filed in the vault.");
      load();
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const iconFor = (ct, iconSize) => {
    if (ct?.startsWith("image/")) return <FileImage size={iconSize} color="#1A3622" />;
    if (ct === "application/pdf") return <FilePdf size={iconSize} color="#6B1718" />;
    return <FileDoc size={iconSize} color="#5C5F58" />;
  };

  const sz = THUMB_SIZES[thumbSize];

  return (
    <div data-testid="vault-page" className="space-y-8">
      <header className="flex items-end justify-between border-b border-[#C9C4B8] pb-6">
        <div>
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2 flex items-center gap-3">
          <span>Appraisal & Document Vault</span>
          <HelpLink anchor="vault" />
        </div>
          <h1 className="font-serif-display text-5xl text-[#0A192F] leading-none">Zero-search life.</h1>
          <p className="text-sm text-[#5C5F58] mt-3 max-w-xl">
            Appraisals, warranties, manuals, receipts — filed once, retrievable forever. No more digging behind appliances for a model number.
          </p>
        </div>
        <label
          data-testid="vault-upload-btn"
          className={`inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm font-medium border border-[#0F2818] cursor-pointer hover:bg-[#0F2818] ${uploading ? "opacity-60 pointer-events-none" : ""}`}
        >
          <UploadSimple size={16} weight="bold" />
          {uploading ? "Filing…" : "Upload File"}
          <input type="file" onChange={onUpload} className="hidden" />
        </label>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="vault-controls">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1" data-testid="vault-filters">
            {KINDS.map((k) => (
              <button
                key={k.value || "all"}
                data-testid={`vault-filter-${k.value || "all"}`}
                onClick={() => setKind(k.value)}
                className={`px-3 py-1.5 text-xs border transition-colors ${
                  kind === k.value
                    ? "bg-[#0A192F] text-[#F9F8F6] border-[#0A192F]"
                    : "bg-white text-[#5C5F58] border-[#E0DDD6] hover:border-[#0A192F] hover:text-[#0A192F]"
                }`}
              >
                {k.label}
              </button>
            ))}
          </div>
          {rooms.length > 0 && (
            <div className="flex items-center gap-2">
              {room && roomPhotos?.[room.toLowerCase()] && (
                <img
                  src={fileUrl(roomPhotos[room.toLowerCase()])}
                  alt=""
                  className="w-7 h-7 object-cover border border-[#E0DDD6] shrink-0"
                  data-testid="vault-room-filter-preview"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
              <select
                value={room}
                onChange={(e) => setRoomPersisted(e.target.value)}
                data-testid="vault-room-filter"
                aria-label="Filter by room"
                className="bg-white border border-[#E0DDD6] px-3 py-1.5 text-xs focus:outline-none focus:border-[#1A3622]"
              >
                <option value="">All rooms</option>
                {rooms.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2" data-testid="vault-thumb-size">
          <span className="font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58]">Thumbnail</span>
          <div className="inline-flex border border-[#E0DDD6] bg-white">
            {(["s", "m", "l"]).map((k) => (
              <button
                key={k}
                onClick={() => setSize(k)}
                data-testid={`vault-thumb-${k}`}
                aria-pressed={thumbSize === k}
                className={`px-2.5 py-1 text-[11px] font-mono-ledger uppercase tracking-[0.15em] border-r border-[#E0DDD6] last:border-r-0 ${
                  thumbSize === k
                    ? "bg-[#0A192F] text-[#F9F8F6]"
                    : "text-[#5C5F58] hover:text-[#0A192F]"
                }`}
              >
                {THUMB_SIZES[k].label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#5C5F58]">Loading…</p>
      ) : files.length === 0 ? (
        <div className="bg-white border border-[#E0DDD6] px-8 py-16 text-center" data-testid="vault-empty">
          <FolderSimple size={28} color="#5C5F58" className="mx-auto mb-4" />
          <p className="font-serif-display text-3xl text-[#0A192F] mb-2">Vault is empty.</p>
          <p className="text-sm text-[#5C5F58] max-w-md mx-auto">
            Scan a warranty. Upload the attic appraisal. Attach the owner&apos;s manual once — find it in ten seconds, forever.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#E0DDD6] bg-white" data-testid="vault-grid">
          {files.map((f) => {
            const isImage = f.content_type?.startsWith("image/");
            const src = `${api.defaults.baseURL}/files/${f.storage_path}`;
            return (
              <a
                key={f.id}
                href={src}
                target="_blank"
                rel="noreferrer"
                data-testid={`vault-file-${f.id}`}
                className="p-5 border-b border-r border-[#E0DDD6] hover:bg-[#F9F8F6]/60 transition-colors flex items-start gap-3"
              >
                <div
                  className="shrink-0 overflow-hidden bg-[#F9F8F6] border border-[#E0DDD6] flex items-center justify-center"
                  style={{ width: sz.box, height: sz.box }}
                  data-testid={`vault-thumb-${f.id}`}
                >
                  {isImage ? (
                    <img
                      src={src}
                      alt={f.original_filename}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fall back to the icon if the file 404s or fails
                        e.currentTarget.style.display = "none";
                        const parent = e.currentTarget.parentElement;
                        if (parent && !parent.dataset.fallbackDrawn) {
                          parent.dataset.fallbackDrawn = "1";
                          const icon = document.createElement("div");
                          icon.className = "text-[#5C5F58]";
                          icon.innerText = "IMG";
                          parent.appendChild(icon);
                        }
                      }}
                    />
                  ) : (
                    iconFor(f.content_type, sz.icon)
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#0A192F] truncate font-medium">{f.original_filename}</div>
                  <div className="font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58] mt-1">
                    {f.kind || "document"} · {(f.size / 1024).toFixed(1)} KB · {fmtDate(f.created_at)}
                  </div>
                </div>
                <DownloadSimple size={14} color="#5C5F58" className="shrink-0" />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
